import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Instagram, Youtube, User, TrendingUp, TrendingDown } from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { PageHeader, SectionCard, DataTableSkeleton } from '../components/ui';
import { API_BASE } from '../api';
import { useDemo } from '../context/DemoContext';
import { DEMO_VENDOR_CREATOR_DETAIL } from '../demo/data';

// ── Types ──────────────────────────────────────────────────────────────────

interface ProductBreakdown {
  product_title: string;
  units: number;
  revenue: number;
  pct_of_total: number;
}

interface RevenueTrend {
  date: string;
  revenue: number;
}

interface CreatorDetail {
  id: number;
  influencer_id: string;
  slug: string;
  stage_name: string;
  platform: string;
  follower_count: number;
  engagement_rate: number;
  profile_image_url: string | null;
  bio: string | null;
  category: string | null;
  orders: number;
  revenue: number;
  avg_order_value?: number;
  avg_creator_revenue?: number;
  revenue_trend: RevenueTrend[];
  products_sold: ProductBreakdown[];
}

// ── Helpers ────────────────────────────────────────────────────────────────

const fmtFollowers = (n: number) =>
  n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)}M`
    : n >= 1_000 ? `${Math.round(n / 1_000)}K`
    : String(n);

const fmtCurrency = (n: number) =>
  n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(1)}M`
    : n >= 1_000 ? `$${(n / 1_000).toFixed(1).replace(/\.0$/, '')}K`
    : `$${n.toLocaleString()}`;

const PLATFORM_STYLES: Record<string, { bg: string; text: string; label: string }> = {
  instagram: { bg: '#E1306C', text: '#fff', label: 'Instagram' },
  tiktok:    { bg: '#000000', text: '#fff', label: 'TikTok' },
  youtube:   { bg: '#FF0000', text: '#fff', label: 'YouTube' },
};

function PlatformBadge({ platform }: { platform: string }) {
  const style = PLATFORM_STYLES[platform?.toLowerCase()] ?? { bg: 'rgba(255,255,255,0.1)', text: '#fff', label: platform };
  return (
    <span
      className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider"
      style={{ background: style.bg, color: style.text, borderRadius: '2px' }}
    >
      <PlatformIcon platform={platform} />
      {style.label}
    </span>
  );
}

function PlatformIcon({ platform }: { platform: string }) {
  const p = platform?.toLowerCase();
  if (p === 'instagram') return <Instagram className="w-3 h-3" />;
  if (p === 'youtube') return <Youtube className="w-3 h-3" />;
  if (p === 'tiktok') return (
    <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.3 6.3 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.76a8.21 8.21 0 0 0 4.79 1.52V6.84a4.85 4.85 0 0 1-1.02-.15z" />
    </svg>
  );
  return <User className="w-3 h-3" />;
}

function AvatarLarge({ src, name }: { src: string; name: string }) {
  const [failed, setFailed] = useState(false);
  if (failed || !src) {
    return (
      <div className="w-16 h-16 rounded-full bg-white/[0.06] border border-white/[0.08] flex items-center justify-center text-white/30 shrink-0">
        <User className="w-7 h-7" />
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={name}
      referrerPolicy="no-referrer"
      onError={() => setFailed(true)}
      className="w-16 h-16 rounded-full object-cover border-2 border-white/[0.10] shrink-0"
      style={{ background: 'rgba(255,255,255,0.04)' }}
    />
  );
}

// Custom tooltip for Recharts
function ChartTooltip({ active, payload, label }: { active?: boolean; payload?: Array<{ value: number }>; label?: string }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#1a1a1a] border border-white/[0.08] px-3 py-2 text-xs" style={{ borderRadius: '2px' }}>
      <p className="text-white/40 mb-1">{label}</p>
      <p className="text-white font-semibold">{fmtCurrency(payload[0].value)}</p>
    </div>
  );
}

// ── Component ──────────────────────────────────────────────────────────────

export default function VendorCreatorDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation(['creators', 'common']);
  const { isDemoMode } = useDemo();

  const [creator, setCreator] = useState<CreatorDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isDemoMode) {
      const demo = DEMO_VENDOR_CREATOR_DETAIL as CreatorDetail | undefined;
      setCreator(demo ?? null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const res = await fetch(`${API_BASE}/vendor/influencers/${encodeURIComponent(id ?? '')}`, {
          credentials: 'include',
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!cancelled) setCreator(json.data ?? null);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [id, isDemoMode]);

  if (loading) {
    return (
      <div className="p-6 lg:p-8 max-w-5xl mx-auto">
        <PageHeader
          title={t('creators:detail.title')}
          back={{ label: t('creators:detail.back'), onClick: () => navigate('/vendor/creators') }}
        />
        <SectionCard><DataTableSkeleton rows={8} cols={4} /></SectionCard>
      </div>
    );
  }

  if (error || !creator) {
    return (
      <div className="p-6 lg:p-8 max-w-5xl mx-auto">
        <PageHeader
          title={t('creators:detail.title')}
          back={{ label: t('creators:detail.back'), onClick: () => navigate('/vendor/creators') }}
        />
        <SectionCard>
          <p className="text-red-400 text-sm py-8 text-center">
            {error ?? t('common:noResults')}
          </p>
        </SectionCard>
      </div>
    );
  }

  const avatarUrl = creator.profile_image_url
    || `https://api.dicebear.com/8.x/avataaars/svg?seed=${encodeURIComponent(creator.influencer_id)}&backgroundColor=ffd400`;

  const comparisonPct = creator.avg_creator_revenue
    ? Math.round(((creator.revenue - creator.avg_creator_revenue) / creator.avg_creator_revenue) * 100)
    : 0;
  const isAboveAvg = comparisonPct >= 0;
  const totalProductRevenue = creator.products_sold?.reduce((s, p) => s + p.revenue, 0) || 1;

  return (
    <div className="p-6 lg:p-8 max-w-5xl mx-auto">
      <PageHeader
        title={t('creators:detail.title')}
        back={{ label: t('creators:detail.back'), onClick: () => navigate('/vendor/creators') }}
      />

      {/* Profile header card */}
      <SectionCard className="mb-5">
        <div className="flex items-start gap-5 flex-wrap">
          <AvatarLarge src={avatarUrl} name={creator.stage_name} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className="text-xl font-display font-bold text-white">{creator.stage_name}</h2>
              <PlatformBadge platform={creator.platform} />
            </div>
            <p className="text-sm text-white/40 mt-1">@{creator.influencer_id}</p>
            {creator.category && (
              <p className="text-xs text-white/35 mt-1 capitalize">{creator.category}</p>
            )}
            {creator.bio && (
              <p className="text-sm text-white/50 mt-3 leading-relaxed max-w-xl">{creator.bio}</p>
            )}
          </div>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6 pt-5 border-t border-white/[0.06]">
          <MiniStat label={t('creators:followers')} value={fmtFollowers(creator.follower_count)} />
          <MiniStat label={t('creators:engagement')} value={`${(creator.engagement_rate * 100).toFixed(1)}%`} />
          <MiniStat label={t('creators:revenue')} value={fmtCurrency(creator.revenue)} accent />
          <MiniStat label={t('creators:orders')} value={creator.orders.toLocaleString()} />
        </div>
      </SectionCard>

      {/* Comparison callout */}
      <div
        className={`flex items-center gap-3 px-4 py-3 mb-5 border text-sm ${
          isAboveAvg
            ? 'bg-[#4ade80]/[0.06] border-[#4ade80]/20 text-[#4ade80]'
            : 'bg-[#f87171]/[0.06] border-[#f87171]/20 text-[#f87171]'
        }`}
        style={{ borderRadius: '2px' }}
      >
        {isAboveAvg ? <TrendingUp className="w-4 h-4 shrink-0" /> : <TrendingDown className="w-4 h-4 shrink-0" />}
        <span>
          {t('creators:detail.title')}: <strong>{Math.abs(comparisonPct)}%</strong>{' '}
          {isAboveAvg ? t('creators:above') : t('creators:below')}{' '}
          {t('creators:comparedToAvg')}
        </span>
      </div>

      {/* Revenue trend chart */}
      {creator.revenue_trend && creator.revenue_trend.length > 0 && (
        <SectionCard title={t('creators:detail.revenueTrend')} subtitle={t('creators:detail.last30d')} className="mb-5">
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={creator.revenue_trend} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="goldGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#FFD400" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#FFD400" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                <XAxis
                  dataKey="date"
                  tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(d: string) => d.slice(5)}
                />
                <YAxis
                  tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(v: number) => `$${(v / 1000).toFixed(0)}K`}
                  width={48}
                />
                <Tooltip content={<ChartTooltip />} />
                <Area
                  type="monotone"
                  dataKey="revenue"
                  stroke="#FFD400"
                  strokeWidth={2}
                  fill="url(#goldGrad)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </SectionCard>
      )}

      {/* Product breakdown */}
      {creator.products_sold && creator.products_sold.length > 0 && (
        <SectionCard title={t('creators:productBreakdown')} noPadding>
          <table className="w-full text-sm">
            <colgroup>
              <col style={{ width: '40%' }} />
              <col style={{ width: '18%' }} />
              <col style={{ width: '22%' }} />
              <col style={{ width: '20%' }} />
            </colgroup>
            <thead>
              <tr className="table-header">
                <th className="text-left px-5 py-3 text-[11px] font-semibold uppercase tracking-wider text-white/40">
                  {t('common:name')}
                </th>
                <th className="text-right px-5 py-3 text-[11px] font-semibold uppercase tracking-wider text-white/40">
                  {t('creators:detail.products')}
                </th>
                <th className="text-right px-5 py-3 text-[11px] font-semibold uppercase tracking-wider text-white/40">
                  {t('creators:revenue')}
                </th>
                <th className="text-right px-5 py-3 text-[11px] font-semibold uppercase tracking-wider text-white/40">
                  % {t('common:total')}
                </th>
              </tr>
            </thead>
            <tbody>
              {creator.products_sold.map((product, i) => {
                const pct = totalProductRevenue > 0
                  ? ((product.revenue / totalProductRevenue) * 100).toFixed(1)
                  : '0.0';
                return (
                  <tr key={i} className="table-row border-t border-white/[0.04]">
                    <td className="px-5 py-3 text-white font-medium truncate max-w-[200px]">
                      {product.product_title}
                    </td>
                    <td className="px-5 py-3 text-right text-white/55">
                      {product.units.toLocaleString()}
                    </td>
                    <td className="px-5 py-3 text-right text-white font-medium">
                      {fmtCurrency(product.revenue)}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <div className="w-16 h-1.5 bg-white/[0.06] overflow-hidden" style={{ borderRadius: '1px' }}>
                          <div
                            className="h-full bg-[#FFD400]"
                            style={{ width: `${Math.min(parseFloat(pct), 100)}%` }}
                          />
                        </div>
                        <span className="text-white/40 text-xs tabular-nums w-12 text-right">{pct}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </SectionCard>
      )}
    </div>
  );
}

// ── Small stat used in the profile header ──────────────────────────────────

function MiniStat({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div>
      <p className="text-[10px] text-white/35 uppercase tracking-wider font-semibold">{label}</p>
      <p className={`text-lg font-bold mt-0.5 ${accent ? 'text-[#FFD400]' : 'text-white'}`}>{value}</p>
    </div>
  );
}
